import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Keeps one product specification synchronized with the selected variant.
 * The section-rendering response remains the source of truth for metafield output.
 *
 * @extends {Component}
 */
class CustomVariantSpecification extends Component {
  /** @type {Element | null} */
  #eventTarget = null;

  connectedCallback() {
    super.connectedCallback();
    this.#eventTarget = this.closest('.shopify-section, dialog');
    this.#eventTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#eventTarget?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#eventTarget = null;
  }

  /** @param {ProductSelectEvent} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { newProduct } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const blockId = this.dataset.blockId;
        if (!blockId) return;

        const updatedSpecification = detail.html.querySelector(
          `custom-variant-specification[data-block-id="${CSS.escape(blockId)}"]`
        );
        if (!(updatedSpecification instanceof HTMLElement)) return;

        this.hidden = updatedSpecification.hidden;
        this.dataset.productId = updatedSpecification.dataset.productId ?? this.dataset.productId;
        this.replaceChildren(...Array.from(updatedSpecification.childNodes, (node) => node.cloneNode(true)));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[custom-variant-specification] Product update failed:', error);
        }
      });
  };
}

if (!customElements.get('custom-variant-specification')) {
  customElements.define('custom-variant-specification', CustomVariantSpecification);
}
